import React, { useCallback, useState } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Line,
  ComposedChart,
  Cell,
  PieChart,
  Pie,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ReferenceLine,
} from 'recharts';
import { Info, ChevronDown, ChevronUp, Minus, Plus, Download } from 'lucide-react';
import { formatCurrency, formatCompactCurrency, compactCurrencyTick, isSafeModeActive } from '../lib/calculations';
import type { ProjectPnlSummary, ProjectMonthlyResults, MonthlyPnlTrend, CostCategory } from '../hooks/useDashboardPnl';
import { calculateTotalsPnl } from '../services/pnlCalculationService';
import { usePnlCategories } from '../hooks/usePnlCategories';
import { xlsxAoaRows } from '../features/pnlAnalytics/spreadsheetSafety';
import { HowWorkedOut } from './howWorkedOut/HowWorkedOut';
import type { MetricDefinition } from './howWorkedOut/metricDefinition';
import { PNL_DASHBOARD_FIGURES } from '../hooks/useDashboardPnl.metrics';

// ─────────────────────────────────────────────────────────────────────────────
// Animation keyframes (injected once)
// ─────────────────────────────────────────────────────────────────────────────

const chartAnimationStyles = `
@keyframes pnlFadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
.pnl-chart-card {
  animation: pnlFadeInUp 0.5s ease-out both;
}
.pnl-chart-card:nth-child(2) { animation-delay: 0.08s; }
.pnl-chart-card:nth-child(3) { animation-delay: 0.16s; }
.pnl-chart-card:nth-child(4) { animation-delay: 0.24s; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Shared styling
// ─────────────────────────────────────────────────────────────────────────────

const chartCardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #fff)',
  borderRadius: 16,
  padding: '1.5rem',
  border: '1px solid var(--border-color, #e5e7eb)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const headerStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'var(--text-primary, #111827)',
  marginBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

// ─────────────────────────────────────────────────────────────────────────────
// Chart Info Panel: "How to interpret this chart" (Enhanced for beginners)
// ─────────────────────────────────────────────────────────────────────────────

interface ChartInfoPanelProps {
  title: string;
  description: string;
  interpretation: string[];
  example?: string;
  whyItMatters?: string;
  tips?: string[];
}

// SEQ-240: formulas moved to the "How is this worked out" pop-up beside each
// chart heading (useDashboardPnl.metrics.ts). This panel keeps the reading guide.
function ChartInfoPanel({ title, description, interpretation, example, whyItMatters, tips }: ChartInfoPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.76rem',
    fontWeight: 700,
    color: '#5b21b6',
    marginTop: 10,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  };

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: isOpen ? 'var(--bg-tertiary, #f9fafb)' : 'none',
          border: `1px solid ${isOpen ? '#a78bfa' : 'var(--border-color, #e5e7eb)'}`,
          borderRadius: 8,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: '0.75rem',
          color: isOpen ? '#7c3aed' : 'var(--text-secondary, #6b7280)',
          transition: 'all 0.2s ease',
          width: '100%',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--bg-tertiary, #f9fafb)';
          (e.currentTarget as HTMLElement).style.borderColor = '#a78bfa';
          (e.currentTarget as HTMLElement).style.color = '#7c3aed';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            (e.currentTarget as HTMLElement).style.background = 'none';
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color, #e5e7eb)';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary, #6b7280)';
          }
        }}
      >
        <Info size={13} />
        How to interpret this chart
        {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {isOpen && (
        <div
          style={{
            marginTop: 8,
            padding: '16px 18px',
            background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
            borderRadius: 12,
            border: '1px solid #ddd6fe',
            fontSize: '0.8rem',
            lineHeight: 1.65,
            color: 'var(--text-primary, #374151)',
            animation: 'pnlFadeInUp 0.25s ease-out',
          }}
        >
          {/* Title */}
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#5b21b6', fontSize: '0.85rem' }}>
            📖 {title}
          </div>

          {/* Description */}
          <p style={{ margin: '0 0 6px', color: 'var(--text-secondary, #6b7280)', fontSize: '0.79rem' }}>
            {description}
          </p>


          {/* Example (simple, concrete) */}
          {example && (
            <>
              <div style={sectionTitleStyle}>💡 Example</div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.6)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: '0.77rem',
                  color: '#374151',
                  border: '1px solid #e9d5ff',
                  lineHeight: 1.6,
                }}
              >
                {example}
              </div>
            </>
          )}

          {/* How to read */}
          <div style={sectionTitleStyle}>📊 How to Read</div>
          <ul style={{ margin: '2px 0 0 16px', paddingLeft: 0, listStyle: 'disc', fontSize: '0.77rem' }}>
            {interpretation.map((item, i) => (
              <li key={i} style={{ marginBottom: 3 }}>{item}</li>
            ))}
          </ul>

          {/* Why it matters */}
          {whyItMatters && (
            <>
              <div style={sectionTitleStyle}>🎯 Why This Matters</div>
              <p style={{ margin: 0, fontSize: '0.77rem', color: '#374151' }}>{whyItMatters}</p>
            </>
          )}

          {/* Pro tips */}
          {tips && tips.length > 0 && (
            <>
              <div style={sectionTitleStyle}>💎 Pro Tips</div>
              <ul style={{ margin: '2px 0 0 16px', paddingLeft: 0, listStyle: '\'→ \'', fontSize: '0.77rem' }}>
                {tips.map((tip, i) => (
                  <li key={i} style={{ marginBottom: 3, paddingLeft: 4 }}>{tip}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation style injector
// ─────────────────────────────────────────────────────────────────────────────

export function PnlChartAnimationStyles() {
  return <style>{chartAnimationStyles}</style>;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. P&L Monthly Trend: Income vs Costs area chart + Profit line  
// ─────────────────────────────────────────────────────────────────────────────

interface PnlTrendChartProps {
  data: MonthlyPnlTrend[];
}

export function PnlTrendChart({ data }: PnlTrendChartProps) {
  if (!data.length) {
    return (
      <div style={chartCardStyle} className="pnl-chart-card">
        <div style={headerStyle}>
          Monthly P&L Trend
          <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.monthly-trend"]} />
        </div>
        <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted, #9ca3af)' }}>
          No monthly P&L data available yet.
        </div>
      </div>
    );
  }

  return (
    <div style={chartCardStyle} className="pnl-chart-card">
      <div style={headerStyle}>
        Monthly P&L Trend
        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.monthly-trend"]} />
      </div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="costsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color, #e5e7eb)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={compactCurrencyTick}
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={((value: number | undefined, name: string) => [
                formatCurrency(value ?? 0),
                name === 'income' ? 'Income' : name === 'costs' ? 'Costs' : 'Net Profit',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Tooltip formatter signature mismatch
              ]) as any}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '0.85rem',
              }}
              wrapperStyle={{ zIndex: 100 }}
            />
            <Legend
              formatter={(value) =>
                value === 'income' ? 'Income' : value === 'costs' ? 'Costs' : 'Net Profit'
              }
              wrapperStyle={{ fontSize: '0.8rem' }}
            />
            <Area
              type="monotone"
              dataKey="income"
              fill="url(#incomeGrad)"
              stroke="#3b82f6"
              strokeWidth={2}
              name="income"
            />
            <Area
              type="monotone"
              dataKey="costs"
              fill="url(#costsGrad)"
              stroke="#ef4444"
              strokeWidth={2}
              name="costs"
            />
            <Line
              type="monotone"
              dataKey="profit"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={{ fill: '#10b981', r: 3 }}
              activeDot={{ r: 6, fill: '#10b981' }}
              name="profit"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <ChartInfoPanel
        title="Monthly P&L Trend"
        description="Think of this as your company's financial heartbeat over time. The blue area is money coming in (income), the red area is money going out (costs), and the green line is what's left over (profit)."
        example={'If your income in March is $500,000 and costs are $400,000, the green profit line sits at $100,000. If costs jump to $480,000 in April while income stays flat, profit drops to $20,000 and the green line dips down, signaling trouble.'}
        interpretation={[
          'Blue area above red = profitable month. The bigger the gap, the better.',
          'Green line trending UP = your margins are improving month-over-month 📈',
          'Green line trending DOWN = costs growing faster than income. Investigate immediately',
          'Blue and red getting closer together = margins are being squeezed',
          'Hover over any point to see exact dollar values for that month',
        ]}
        whyItMatters="This chart is your early warning system. A single bad month might be normal, but 2-3 months of declining green line means you need to act: review project budgets, renegotiate supplier contracts, or address staffing inefficiencies before profits disappear."
        tips={[
          'Compare month-over-month and look for patterns (e.g., seasonal cost spikes in summer)',
          'A sudden spike in red area could indicate unplanned material price increases',
          'Flat green line with rising blue and red = you\'re growing revenue but not improving efficiency',
        ]}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Project Profit Comparison: Horizontal bars, clickable
// ─────────────────────────────────────────────────────────────────────────────

interface ProjectProfitChartProps {
  data: ProjectPnlSummary[];
}

export function ProjectProfitChart({ data }: ProjectProfitChartProps) {
  const navigate = useNavigate();

  const chartData = [...data]
    .sort((a, b) => b.netProfit - a.netProfit)
    .slice(0, 8)
    .map((p) => ({
      ...p,
      displayName: p.projectName.length > 18 ? p.projectName.substring(0, 18) + '…' : p.projectName,
    }));

  // Direct Bar onClick: reliable, receives data entry directly
  const handleBarClick = useCallback(
    (_data: unknown, index: number) => {
      const entry = chartData[index];
      if (entry?.projectId) {
        navigate(`/projects/${entry.projectId}`);
      }
    },
    [navigate, chartData],
  );

  if (!chartData.length) {
    return (
      <div style={chartCardStyle} className="pnl-chart-card">
        <div style={headerStyle}>
          Project Profit Comparison
          <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.project-profit"]} />
        </div>
        <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted, #9ca3af)' }}>
          No project P&L data available yet.
        </div>
      </div>
    );
  }

  return (
    <div style={chartCardStyle} className="pnl-chart-card">
      <div style={headerStyle}>
        Project Profit Comparison
        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.project-profit"]} />
        <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted, #9ca3af)', marginLeft: 'auto' }}>
          Click bar to view project →
        </span>
      </div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-color, #e5e7eb)" />
            <XAxis
              type="number"
              tickFormatter={compactCurrencyTick}
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="displayName"
              width={120}
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
            />
            <Tooltip
              formatter={((value: number | undefined, _name: string, props: { payload?: Record<string, number> }) => {
                const entry = props?.payload as ProjectPnlSummary | undefined;
                return [
                  `${formatCurrency(value ?? 0)} (${entry?.profitPercent?.toFixed(1) ?? '0'}% margin)`,
                  'Net Profit',
                ];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Tooltip formatter signature mismatch
              }) as any}
              labelFormatter={(label) => `Project: ${label}`}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '0.85rem',
              }}
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              wrapperStyle={{ zIndex: 100 }}
            />
            <Bar
              dataKey="netProfit"
              name="Net Profit"
              radius={[0, 6, 6, 0]}
              barSize={20}
              cursor="pointer"
              onClick={handleBarClick}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`profit-cell-${index}`}
                  fill={entry.netProfit >= 0 ? '#10b981' : '#ef4444'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartInfoPanel
        title="Project Profit Comparison"
        description="Think of this as a leaderboard for your projects. Each bar represents how much money a project made (green) or lost (red) after all expenses. Projects are ranked from highest to lowest profit."
        example={'Project A earned $2M in claims and spent $1.7M in costs → Net Profit = $300K (green bar). Project B earned $500K but spent $520K → Net Loss = -$20K (red bar). Project A is your star performer; Project B needs urgent attention.'}
        interpretation={[
          'Longer green bar = that project is contributing more profit to the business',
          'Red bars = projects actively losing money. These need immediate management review',
          'Click any bar to jump directly to that project\'s detailed P&L breakdown',
          'Hover to see the exact profit amount and margin percentage',
        ]}
        whyItMatters="Not all projects perform equally. This chart instantly shows which projects are your cash cows and which are bleeding money. Management should focus reviews on the bottom performers. Even flipping one loss-maker to breakeven can significantly impact overall portfolio profitability."
        tips={[
          'Healthy margin is >10%. If most bars show <5%, your pricing strategy needs review',
          'A short green bar with high income might still be okay. Check the margin % in the tooltip',
          'Compare this chart monthly to catch projects trending from green toward red early',
        ]}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Cost Breakdown: Donut Chart + Stacked Bar (replaces Treemap)
// ─────────────────────────────────────────────────────────────────────────────

interface CostBreakdownTreemapProps {
  data: CostCategory[];
}

// Custom donut label renderer: shows name+% outside the chart for all slices
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts PieLabelRenderProps doesn't include custom data fields
const renderDonutLabel = (props: Record<string, any>) => {
  const { cx, cy, midAngle, outerRadius, name, percentage } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="var(--text-primary, #374151)"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {name} ({percentage}%)
    </text>
  );
};

export function CostBreakdownTreemap({ data }: CostBreakdownTreemapProps) {
  const totalCost = data.reduce((sum, c) => sum + c.value, 0);

  if (totalCost === 0) {
    return (
      <div style={chartCardStyle} className="pnl-chart-card">
        <div style={headerStyle}>
          Cost Distribution
          <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.cost-distribution"]} />
        </div>
        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted, #9ca3af)' }}>
          No cost data available yet.
        </div>
      </div>
    );
  }

  const donutData = data
    .filter(c => c.value > 0)
    .sort((a, b) => b.value - a.value)
    .map(c => ({
      name: c.name,
      value: c.value,
      color: c.color,
      percentage: ((c.value / totalCost) * 100).toFixed(1),
    }));

  return (
    <div style={chartCardStyle} className="pnl-chart-card">
      <div style={headerStyle}>
        Cost Distribution
        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.cost-distribution"]} />
      </div>

      {/* Donut Chart: minAngle ensures tiny categories like 0.3% are visible */}
      <div style={{ width: '100%', height: 200, position: 'relative' }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={78}
              minAngle={18}
              paddingAngle={2}
              strokeWidth={2}
              stroke="rgba(255,255,255,0.8)"
              label={renderDonutLabel}
              labelLine={{ stroke: 'var(--text-muted, #9ca3af)', strokeWidth: 1 }}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
            >
              {donutData.map((entry, index) => (
                <Cell key={`donut-cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number | undefined) => [formatCurrency(value ?? 0), 'Amount']}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '0.85rem',
              }}
              wrapperStyle={{ zIndex: 100 }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label: total cost */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 500, lineHeight: 1 }}>Total</div>
          <div data-seq-private="" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary, #111827)', marginTop: 2 }}>
            {formatCompactCurrency(totalCost)}
          </div>
        </div>
      </div>

      {/* Stacked Horizontal Bar: guarantees visibility for all categories */}
      <div
        style={{
          display: 'flex',
          height: 14,
          borderRadius: 99,
          overflow: 'hidden',
          background: 'var(--bg-tertiary, #f3f4f6)',
          marginTop: 8,
        }}
      >
        {donutData.map((c) => {
          const rawPct = (c.value / totalCost) * 100;
          // Enforce min 3% visual width so even 0.3% "Other" is visible
          const displayPct = Math.max(rawPct, 3);
          return (
            <div
              key={c.name}
              data-seq-private=""
              title={`${c.name}: ${c.percentage}% (${formatCurrency(c.value)})`}
              style={{
                width: `${displayPct}%`,
                background: c.color,
                transition: 'width 0.6s ease',
                cursor: 'default',
                position: 'relative',
              }}
            />
          );
        })}
      </div>

      {/* Category Cards */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {donutData.map((c) => (
          <div
            key={c.name}
            style={{
              flex: '1 1 0',
              minWidth: 120,
              background: 'var(--bg-tertiary, #f9fafb)',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 10,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: c.color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary, #374151)' }}>
                {c.name}
              </span>
            </div>
            <div data-seq-private="" style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary, #111827)', marginTop: 2 }}>
              {formatCompactCurrency(c.value)}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)' }}>
              {c.percentage}% of total costs
            </div>
            {/* Mini proportion bar */}
            <div style={{ height: 4, borderRadius: 2, background: 'var(--border-color, #e5e7eb)', marginTop: 4 }}>
              <div
                style={{
                  height: '100%',
                  borderRadius: 2,
                  background: c.color,
                  width: `${Math.max(parseFloat(c.percentage), 2)}%`,
                  transition: 'width 0.6s ease',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <ChartInfoPanel
        title="Cost Distribution (Donut Chart)"
        description="This donut chart shows where your construction budget is being spent. Each colored slice represents a cost category. The larger the slice, the more money flows there. All categories are visible, even the smallest ones."
        example={'If total costs are $10M and Materials = $3.5M, Wages = $4M, Other = $2.5M, then Materials is 35%, Wages is 40%, Other is 25%. The Wages slice would be the largest, signaling that labor cost optimization could have the biggest impact on your bottom line.'}
        interpretation={[
          'Largest slice = your #1 cost driver. Cost reduction efforts here get the biggest ROI',
          'Materials (amber) should typically be 25-35%. Above 40%? Material prices may be inflated.',
          'Wages (blue) typically 30-40%. Above 45%? Check for overtime or overstaffing.',
          'Other (purple) includes subcontractors, equipment, overheads. Watch for hidden cost creep here',
          'Center shows your total portfolio cost. Hover any slice for the exact dollar amount',
        ]}
        whyItMatters="You can\'t manage what you can\'t see. If 70% of your money goes to two categories, that\'s where a 5% efficiency improvement would save hundreds of thousands. This chart tells you exactly where to focus negotiations, process improvements, or resource reallocation."
        tips={[
          'Compare this across quarters. If one slice is growing while others shrink, investigate why',
          'If \'Other\' is unusually large, drill into that category to see what\'s inside',
          'Use the cards below the chart for exact figures in management meetings',
        ]}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Profit Margin Radial: Per-project margins as concentric rings
// ─────────────────────────────────────────────────────────────────────────────

interface ProfitMarginRadialProps {
  data: ProjectPnlSummary[];
}

const RADIAL_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899'];

export function ProfitMarginRadial({ data }: ProfitMarginRadialProps) {
  const radialData = [...data]
    .filter(p => p.constructionIncome > 0)
    .sort((a, b) => b.profitPercent - a.profitPercent)
    .slice(0, 6)
    .map((p, i) => ({
      name: p.projectName.length > 16 ? p.projectName.substring(0, 16) + '…' : p.projectName,
      margin: Math.max(0, Math.min(p.profitPercent, 100)), // clamp 0-100 for display
      rawMargin: p.profitPercent,
      fill: RADIAL_COLORS[i % RADIAL_COLORS.length],
    }));

  if (!radialData.length) {
    return (
      <div style={chartCardStyle} className="pnl-chart-card">
        <div style={headerStyle}>
          Profit Margins by Project
          <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.profit-margins"]} />
        </div>
        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted, #9ca3af)' }}>
          No profit margin data available yet.
        </div>
      </div>
    );
  }

  return (
    <div style={chartCardStyle} className="pnl-chart-card">
      <div style={headerStyle}>
        Profit Margins by Project
        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.profit-margins"]} />
      </div>
      <div style={{ width: '100%', flex: 1, minHeight: 220 }}>
        <ResponsiveContainer>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="20%"
            outerRadius="90%"
            data={radialData}
            startAngle={180}
            endAngle={0}
          >
            <PolarAngleAxis type="number" domain={[0, 40]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: 'var(--bg-tertiary, #f3f4f6)' }}
              dataKey="margin"
              cornerRadius={6}
            >
              {radialData.map((entry, index) => (
                <Cell key={`radial-${index}`} fill={entry.fill} />
              ))}
            </RadialBar>
            <Tooltip
              formatter={((_value: number | undefined, _name: string, props: { payload: Record<string, number | string> }) => [
                `${Number(props.payload.rawMargin).toFixed(1)}%`,
                String(props.payload.name),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Tooltip formatter signature mismatch
              ]) as any}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '0.85rem',
              }}
              wrapperStyle={{ zIndex: 100 }}
            />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 }}>
        {radialData.map((p) => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.fill }} />
            <span style={{ color: 'var(--text-secondary, #6b7280)' }}>{p.name}</span>
            <span style={{ fontWeight: 600, color: p.rawMargin >= 10 ? '#10b981' : p.rawMargin >= 5 ? '#f59e0b' : '#ef4444' }}>
              {p.rawMargin.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
      <ChartInfoPanel
        title="Profit Margins by Project"
        description="Each arc represents a project. Think of it like a speedometer: longer arcs mean higher profit margins. This lets you compare how efficiently each project converts income into profit at a glance."
        example={'If Project X has income of $1M and net profit of $150K, its margin is 15% (a healthy, long green arc). If Project Y earns $800K but profits only $30K, its margin is 3.75%, a short red arc indicating that project is barely profitable despite high revenue.'}
        interpretation={[
          '🟢 Green (≥10%) = Healthy. The project is making good money relative to its size',
          '🟡 Amber (5-10%) = At Risk. Margins are thin, any cost surprise could push it into loss',
          '🔴 Red (<5%) = Critical. The project may be losing money after hidden costs',
          'Industry benchmark: 10-20% margin is considered healthy for construction',
          'Hover over any arc to see the exact margin percentage',
        ]}
        whyItMatters="Revenue is vanity, profit is sanity. A $5M project with 3% margin ($150K profit) is worse than a $1M project with 20% margin ($200K profit). This chart helps management identify which projects are truly efficient vs. which are just big but unprofitable."
        tips={[
          'If all arcs are similar lengths, your projects are well-balanced. If one is much shorter, investigate.',
          'A project with high revenue but low margin might be taking on too much risk for too little reward',
          'Compare this over time. Shrinking arcs mean margins are being eroded',
        ]}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Pareto Analysis Chart: 80/20 Multi-Metric Analysis
// ─────────────────────────────────────────────────────────────────────────────

type ParetoMetric = 'cost' | 'income' | 'profit' | 'patching';

interface ParetoAnalysisProps {
  data: ProjectPnlSummary[];
  costBreakdown: CostCategory[];
  projectMonthlyData?: ProjectMonthlyResults[];
  /** When true, locks the chart to 'patching' metric and hides the metric switcher (used in Safe Mode). */
  patchingOnly?: boolean;
}

const PARETO_BAR_COLORS: Record<ParetoMetric, string[]> = {
  cost:     ['#ef4444', '#f87171', '#fca5a5', '#fee2e2', '#fff1f2', '#fff5f5', '#fffafa', '#ffffff'],
  income:   ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff', '#f5f9ff', '#ffffff'],
  profit:   ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#ecfdf5', '#f5fdfb', '#ffffff'],
  patching: ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5', '#fff7ed', '#fffbf5', '#ffffff'],
};

const PARETO_METRIC_CONFIG: Record<ParetoMetric, {
  label: string;
  color: string;
  lightBg: string;
  borderColor: string;
  valueKey: keyof ProjectPnlSummary;
  tooltipLabel: string;
  tableHeader: string;
  badgeText: (count: number, total: number, pct: string) => string;
}> = {
  cost: {
    label: 'Cost',
    color: '#ef4444',
    lightBg: '#fef2f2',
    borderColor: '#fecaca',
    valueKey: 'totalDeductions',
    tooltipLabel: 'Project Cost',
    tableHeader: 'Cost',
    badgeText: (count, total, pct) => `${count} of ${total} projects = ~80% of costs (${pct}% of projects)`,
  },
  income: {
    label: 'Income',
    color: '#3b82f6',
    lightBg: '#eff6ff',
    borderColor: '#bfdbfe',
    valueKey: 'constructionIncome',
    tooltipLabel: 'Project Income',
    tableHeader: 'Income',
    badgeText: (count, total, pct) => `${count} of ${total} projects = ~80% of income (${pct}% of projects)`,
  },
  profit: {
    label: 'Profit',
    color: '#10b981',
    lightBg: '#ecfdf5',
    borderColor: '#a7f3d0',
    valueKey: 'netProfit',
    tooltipLabel: 'Net Profit',
    tableHeader: 'Net Profit',
    badgeText: (count, total, pct) => `${count} of ${total} projects = ~80% of profit (${pct}% of projects)`,
  },
  patching: {
    label: 'Patching',
    color: '#f97316',
    lightBg: '#fff7ed',
    borderColor: '#fed7aa',
    valueKey: 'patchingCost',
    tooltipLabel: 'Patching Cost',
    tableHeader: 'Patching',
    badgeText: (count, total, pct) => `${count} of ${total} projects = ~80% of patching (${pct}% of projects)`,
  },
};

// SEQ-240: the heading's "How is this worked out" text follows the selected measure.
const PARETO_FIGURE_BY_METRIC: Record<ParetoMetric, MetricDefinition> = {
  cost: PNL_DASHBOARD_FIGURES["pnl.pareto.cost"],
  income: PNL_DASHBOARD_FIGURES["pnl.pareto.income"],
  profit: PNL_DASHBOARD_FIGURES["pnl.pareto.profit"],
  patching: PNL_DASHBOARD_FIGURES["pnl.pareto.patching"],
};

function getParetoInfoContent(metric: ParetoMetric): Omit<ChartInfoPanelProps, 'title'> {
  switch (metric) {
    case 'cost':
      return {
        description: `The 80/20 rule (Pareto Principle) says roughly 80% of effects come from 20% of causes. This chart sorts your projects from highest cost to lowest and highlights the few that drive most of your total spending. The table below shows a month-by-month cost breakdown so you can see exactly when money was spent. Hover any value to see the full dollar amount and how it compares to income.`,
        example: 'Suppose you manage 10 projects with $20M total costs. This chart might show: Project A ($6M = 30%), Project B ($5M = 55% cumulative), Project C ($4M = 75%). Just 3 of 10 projects (30%) drive 75% of all costs. These are your "Vital Few." Focusing cost management here delivers 10× more impact than spreading effort evenly.',
        interpretation: [
          '🔴 Red-shaded bars = "Vital Few": the small group of projects that together account for ~80% of total costs. These are your highest-leverage targets.',
          '🔘 Grey bars = "Trivial Many": each contributes a small share individually.',
          '📈 Red cumulative line = shows how costs add up project by project. A steep early rise means costs are heavily concentrated.',
          '🔴 Red dashed "80% Line" = the threshold. Projects to the LEFT of where the cumulative line crosses this are your Vital Few.',
          '📊 Monthly columns in the table = shows each project\'s cost per month. Hover any cell to see the exact dollar amount and what % of that month\'s income it represents.',
          '💡 "Total" column = sum across the selected date range. "% Tot" = this project\'s share of all project costs. "Cum %" = running total from top to bottom.',
          '🎯 Use the Range control (top right) to adjust how many months of data you\'re analyzing. Default is 6 months.',
        ],
        whyItMatters: "The Pareto principle is the most important concept in cost management. It tells you: don't spread your attention across all projects equally. A 5% cost reduction on a $6M project saves $300K. The same 5% on a $500K project saves only $25K. That's 12× more impact for the same effort. Focus on the Vital Few.",
        tips: [
          'If only 2 projects cross the 80% line = very high concentration risk. If one project has cost overruns, your whole portfolio suffers.',
          'Hover any monthly cell to see the full dollar amount and its percentage of income for that specific month.',
          'Compare this view with Income view. If the same project dominates both, it\'s your highest-stakes project requiring the most oversight.',
          'Use the range selector to zoom in (1-3 months) for recent trends or zoom out (12+ months) for annual planning.',
        ],
      };
    case 'income':
      return {
        description: `This view ranks projects by their construction income (revenue) and reveals which small group of projects generates the lion's share of your earnings. The month-by-month breakdown in the table below shows revenue timing, which is critical for cash flow planning. Hover any value to see the exact dollar amount and portfolio share.`,
        example: 'If your portfolio earns $15M across 8 projects: Project A ($5M = 33%), Project B ($4M = 60% cumulative), Project C ($2.5M = 77%). Just 3 projects (37.5%) generate 77% of income. If Project A is delayed, you instantly lose 33% of your revenue stream, a critical cash flow risk.',
        interpretation: [
          '🔵 Blue-shaded bars = "Revenue Engines": the projects generating ~80% of your total income. Protect these at all costs.',
          '🔘 Grey bars = smaller income contributors. Valuable, but losing one won\'t cripple the portfolio.',
          '📈 Blue cumulative line = shows how income adds up. A steep curve = income is highly concentrated (risky if one project stalls).',
          '📊 Monthly columns = see exactly when each project earned revenue. Months showing "—" had no income recorded.',
          '💡 Hover any monthly value to see the full dollar amount and what % of the total portfolio income it represents.',
          '🎯 "% Tot" = this project\'s share of all income. Hover it to see the exact calculation.',
          '📅 Use Range to see if revenue engines have been consistent over time or if income was front-loaded.',
        ],
        whyItMatters: "If 80% of your income comes from just 2 projects, your entire business depends on them hitting milestones on time. A single delay, dispute, or client issue on a revenue engine can cascade into portfolio-wide cash flow problems. This view helps you plan for and mitigate that concentration risk.",
        tips: [
          'Diversify: If one project dominates >40% of income, actively pursue new projects to reduce risk.',
          'Compare Income Pareto vs Cost Pareto. If the same project is "Vital Few" in both, it\'s your highest-stakes project.',
          'Hover any cell to see the exact amount. Compact values like "$4.2K" expand to "$4,200.00" on hover.',
          'Revenue engines should get priority in resource allocation and claim processing speed.',
        ],
      };
    case 'profit':
      return {
        description: `This view reveals which projects actually make you money. Unlike cost or income views, this shows your true profit generators: the projects that fund operations, bonuses, and growth. The monthly breakdown shows when profits were earned, and hovering any value shows the exact amount and its percentage of income (profit margin).`,
        example: 'Portfolio profit is $2M across 8 projects. Pareto shows: Project A ($800K = 40%), Project B ($500K = 65% cumulative), Project C ($350K = 82.5%). Only 3 projects deliver 80%+ of total profit. If Project A\'s margin drops 5%, you lose $250K in portfolio profit, a massive impact.',
        interpretation: [
          '🟢 Green-shaded bars = "Profit Generators": the projects creating ~80% of your portfolio profit. These are the true value creators.',
          '🔘 Grey bars = marginal profit contributors. May be high-revenue but low-margin (busy but not profitable).',
          '📈 Green cumulative line = shows how profit accumulates across projects.',
          '⚠️ Projects with negative profit (losses) are excluded. Check the Cost view to investigate loss-makers.',
          '📊 Monthly columns = shows when each project was profitable. Hover to see exact profit and what % of income it represents (profit margin).',
          '💡 Hover "% Tot" to see the calculation: "Project profit ÷ Total portfolio profit = X%".',
          '🔄 Hover "Cum %" to see the running total and which projects together reach 80%.',
        ],
        whyItMatters: "Revenue is vanity, profit is reality. A $5M project with 3% margin ($150K profit) contributes far less than a $1M project with 25% margin ($250K profit). This view cuts through revenue illusions and shows which projects actually sustain your business.",
        tips: [
          'If a Revenue Engine (income view) is NOT a Profit Generator here, its margins need urgent review.',
          'Hover monthly values to spot months where a profitable project dipped into loss, an early warning sign.',
          'Focus improvement efforts on Profit Generators first. A 2% margin improvement on a high-profit project has massive impact.',
          'Use this in board meetings: "These 3 projects generate 80% of our profit, here\'s how we\'re protecting them."',
        ],
      };
    case 'patching':
      return {
        description: `This view ranks projects by patching (rework/defect) costs, revealing where your quality problems are costing the most money. Patching is work done twice: it's pure waste. The "% Inc" column shows the defect ratio (patching as a percentage of income). Hover any value to see the full amount and the exact calculation behind the percentage.`,
        example: 'Project Tapestry shows $55.7K patching with 4.3% defect ratio. Hovering the "4.3%" cell reveals the calculation: $55,687 ÷ $1,294,500 = 4.3%. This is above 3%, a critical quality concern. Meanwhile, Project Windemere has $40.9K patching but only 4.0% ratio, still problematic but relatively better per dollar earned.',
        interpretation: [
          '🟠 Orange bars = projects with highest absolute patching costs. These drive the majority of your defect spend.',
          '📊 "% Inc" column = the defect ratio: what percentage of a project\'s income was re-spent on rework. This is the TRUE quality metric.',
          '🟢 Green % Inc = below 1.5%, a healthy quality level.',
          '🟠 Orange % Inc = between 1.5-3%. Quality needs attention.',
          '🔴 Red % Inc = above 3%, a critical quality problem requiring immediate intervention.',
          '💡 Hover any "% Inc" cell to see the full calculation: Patching $ ÷ Income $ = X%.',
          '📅 Monthly columns = shows when patching costs occurred. Hover to see the exact amount and what % of that month\'s income it represents.',
          '🔍 Hover monthly cells to investigate: was the patching concentrated in one month (maybe a single incident) or spread across months (systemic issue)?',
        ],
        whyItMatters: "Every dollar spent on patching is a dollar of profit lost: it's work you've already been paid to do, done again at your own cost. A project with 1% defect ratio is healthy. Above 3% signals systemic quality failures: poor supervision, inadequate materials, or rushed timelines. This view helps you target quality interventions where they'll save the most money.",
        tips: [
          'Hover the "% Inc" header to see the color-coded thresholds explained.',
          'A project with low absolute patching but HIGH defect ratio may be a small project with many defects, so don\'t ignore it.',
          'High patching + high profit may be acceptable short-term; high patching + low profit is an emergency.',
          'Compare monthly patching trends: if patching is increasing month-over-month, quality is degrading.',
          'Projects with "—" in all months may not be tracking defects. Verify data completeness before celebrating.',
        ],
      };
  }
}

export function ParetoAnalysisChart({ data, projectMonthlyData, patchingOnly }: ParetoAnalysisProps) {
  const [metric, setMetric] = useState<ParetoMetric>(patchingOnly ? 'patching' : 'cost');
  const [rangeMonths, setRangeMonths] = useState(6);
  const { categories } = usePnlCategories();
  const config = PARETO_METRIC_CONFIG[metric];

  // ── Safe Mode bypass: when patchingOnly, show real $ values ────────────
  // The global formatCurrency masks ALL dollar values in safe mode, but the
  // whole point of patchingOnly is to let Tom see actual patching costs.
  // These local formatters bypass the safe mode mask.
  const fmt = React.useMemo(() => {
    if (!patchingOnly) {
      return { currency: formatCurrency, compact: formatCompactCurrency, tick: compactCurrencyTick };
    }
    // Unmasked formatters: always show real values
    const unmaskCompact = (amount: number | undefined | null): string => {
      const val = amount ?? 0;
      if (isNaN(val)) return '$0';
      const abs = Math.abs(val);
      const sign = val < 0 ? '-' : '';
      if (abs >= 1_000_000) { const m = abs / 1_000_000; return `${sign}$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`; }
      if (abs >= 1_000) { const k = abs / 1_000; return `${sign}$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`; }
      return `${sign}$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
    };
    const unmaskFull = (amount: number | undefined | null, includeDecimals = false): string => {
      const safeAmount = amount ?? 0;
      if (isNaN(safeAmount)) return '$0';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: 'AUD',
        minimumFractionDigits: includeDecimals ? 2 : 0,
        maximumFractionDigits: includeDecimals ? 2 : 0,
      }).format(safeAmount);
    };
    return {
      currency: unmaskFull,
      compact: unmaskCompact,
      tick: (val: number) => unmaskCompact(val),
    };
  }, [patchingOnly]);

  // ── Date-range-filtered data ──────────────────────────────────────────────
  // When projectMonthlyData is available, re-aggregate per-project data
  // for only the last `rangeMonths` months. Otherwise fall back to `data`.
  const filteredData = React.useMemo<ProjectPnlSummary[]>(() => {
    if (!projectMonthlyData || projectMonthlyData.length === 0 || categories.length === 0) return data;

    const now = new Date();
    const endKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const startDate = new Date(now.getFullYear(), now.getMonth() - rangeMonths + 1, 1);
    const startKey = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;

    return projectMonthlyData.map(pm => {
      const filtered = pm.monthlyResults.filter(mr => mr.key >= startKey && mr.key <= endKey);
      if (filtered.length === 0) {
        return {
          projectId: pm.projectId, projectName: pm.projectName,
          constructionIncome: 0, totalDeductions: 0, netProfit: 0, profitPercent: 0,
          wagesPercent: 0, materialsPercent: 0, materialsCost: 0, wagesCost: 0,
          otherCosts: 0, patchingCost: 0, retention: 0, estimateProfit: 0,
        };
      }
      const totals = calculateTotalsPnl(filtered.map(f => f.result), categories, { materialPercent: 30, wagesMultiplier: 1.04, estimateProfit: 0 });
      const patchingCost = totals.expensesByCategory['Patching'] || 0;
      return {
        projectId: pm.projectId, projectName: pm.projectName,
        constructionIncome: totals.constructionIncome, totalDeductions: totals.totalDeductions,
        netProfit: totals.netProfit, profitPercent: totals.profitPercent,
        wagesPercent: totals.wagesPercent, materialsPercent: totals.materialsPercent,
        materialsCost: totals.materialsBudget, wagesCost: totals.wagesWithMultiplier,
        otherCosts: totals.otherExpensesTotal, patchingCost,
        retention: totals.retention, estimateProfit: 0,
      };
    }).filter(s => s.constructionIncome > 0 || s.totalDeductions > 0);
  }, [data, projectMonthlyData, rangeMonths, categories]);

  // Build driver data based on selected metric
  const valueKey = config.valueKey;
  const drivers = [...filteredData]
    .filter(p => {
      const val = Number(p[valueKey]) || 0;
      return val > 0;
    })
    .sort((a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0))
    .map(p => ({
      name: p.projectName.length > 14 ? p.projectName.substring(0, 14) + '…' : p.projectName,
      fullName: p.projectName,
      value: Number(p[valueKey]) || 0,
      income: p.constructionIncome,
      cost: p.totalDeductions,
      profitMargin: p.profitPercent,
      patchingPercent: p.constructionIncome > 0 ? (p.patchingCost / p.constructionIncome) * 100 : 0,
    }));

  if (drivers.length === 0) {
    return (
      <div style={chartCardStyle} className="pnl-chart-card">
        <div style={headerStyle}>
          Pareto Analysis (80/20 Rule)
          <HowWorkedOut definition={PARETO_FIGURE_BY_METRIC[metric]} />
        </div>
        <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted, #9ca3af)' }}>
          No {config.label.toLowerCase()} data available for Pareto analysis.
        </div>
      </div>
    );
  }

  // Calculate cumulative percentage
  const totalValue = drivers.reduce((s, d) => s + d.value, 0);
  const paretoData = drivers.reduce<Array<typeof drivers[number] & {
    cumulativePercent: number;
    individualPercent: number;
    isVitalFew: boolean;
  }>>((acc, d, i) => {
    const runningTotal = (acc.length > 0
      ? (acc[acc.length - 1].cumulativePercent / 100) * totalValue
      : 0) + d.value;
    const cumulativePercent = (runningTotal / totalValue) * 100;
    const individualPercent = (d.value / totalValue) * 100;
    acc.push({
      ...d,
      cumulativePercent: Math.round(cumulativePercent * 10) / 10,
      individualPercent: Math.round(individualPercent * 10) / 10,
      isVitalFew: cumulativePercent <= 80 || i === 0,
    });
    return acc;
  }, []);

  const vitalFewCount = paretoData.filter(d => d.isVitalFew).length;
  const vitalFewPercent = vitalFewCount > 0
    ? ((vitalFewCount / paretoData.length) * 100).toFixed(0)
    : '0';

  const infoContent = getParetoInfoContent(metric);

  // Pill button style: uses metric-specific color when active
  const pillStyle = (m: ParetoMetric, active: boolean): React.CSSProperties => {
    const mConfig = PARETO_METRIC_CONFIG[m];
    return {
      padding: '5px 14px',
      borderRadius: 20,
      fontSize: '0.73rem',
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
      border: `1px solid ${active ? mConfig.color : 'var(--border-color, #e5e7eb)'}`,
      background: active ? mConfig.color : 'transparent',
      color: active ? '#fff' : 'var(--text-secondary, #6b7280)',
      transition: 'all 0.2s ease',
    };
  };

  // Range control button style
  const rangeBtnStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, borderRadius: '50%', border: '1px solid var(--border-color, #e5e7eb)',
    background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary, #6b7280)',
    fontSize: '0.7rem', transition: 'all 0.15s ease',
  };

  return (
    <div style={chartCardStyle} className="pnl-chart-card">
      <div style={{ ...headerStyle, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Pareto Analysis (80/20 Rule)
          <HowWorkedOut definition={PARETO_FIGURE_BY_METRIC[metric]} />
        </span>

        {/* Metric Switcher: hidden in patchingOnly (Safe Mode) */}
        {!patchingOnly && (
        <div className="pnl-pareto-switcher" style={{ display: 'flex', gap: 4, marginLeft: 'auto', marginRight: 8 }}>
          {(['cost', 'income', 'profit', 'patching'] as ParetoMetric[]).map(m => (
            <button
              key={m}
              onClick={(e) => { e.stopPropagation(); setMetric(m); }}
              style={pillStyle(m, metric === m)}
              onMouseEnter={(e) => {
                if (metric !== m) {
                  (e.currentTarget as HTMLElement).style.borderColor = PARETO_METRIC_CONFIG[m].color;
                  (e.currentTarget as HTMLElement).style.color = PARETO_METRIC_CONFIG[m].color;
                }
              }}
              onMouseLeave={(e) => {
                if (metric !== m) {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color, #e5e7eb)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary, #6b7280)';
                }
              }}
            >
              {PARETO_METRIC_CONFIG[m].label}
            </button>
          ))}
        </div>
        )}

        <span
          style={{
            fontSize: '0.68rem',
            fontWeight: 500,
            color: config.color,
            background: config.lightBg,
            padding: '3px 10px',
            borderRadius: 20,
            border: `1px solid ${config.borderColor}`,
            whiteSpace: 'nowrap',
          }}
        >
          {config.badgeText(vitalFewCount, paretoData.length, vitalFewPercent)}
        </span>

        {/* Range selector, inline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)' }}>Range:</span>
          <button
            style={rangeBtnStyle}
            onClick={() => setRangeMonths(m => Math.max(1, m - 1))}
            aria-label="Decrease range"
          >
            <Minus size={12} />
          </button>
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, minWidth: 50, textAlign: 'center',
            color: 'var(--text-primary, #111827)',
          }}>
            {rangeMonths}mo
          </span>
          <button
            style={rangeBtnStyle}
            onClick={() => setRangeMonths(m => Math.min(60, m + 1))}
            aria-label="Increase range"
          >
            <Plus size={12} />
          </button>
          <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.range"]} />
        </div>
      </div>

      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={paretoData} margin={{ top: 10, right: 40, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color, #e5e7eb)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
              tickLine={false}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis
              yAxisId="value"
              tickFormatter={fmt.tick}
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #6b7280)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="percent"
              orientation="right"
              domain={[0, 100]}
              tickFormatter={(val) => `${val}%`}
              tick={{ fontSize: 11, fill: config.color }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={((value: number | undefined, name: string, props: { payload?: Record<string, number> }) => {
                if (name === 'cumulativePercent') {
                  return [`${value?.toFixed(1)}%`, 'Cumulative %'];
                }
                const entry = props?.payload;
                return [
                  `${fmt.currency(value ?? 0)} (${entry?.individualPercent?.toFixed(1)}% of total)`,
                  config.tooltipLabel,
                ];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Tooltip formatter signature mismatch
              }) as any}
              labelFormatter={(label) => `Project: ${label}`}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '0.82rem',
              }}
              wrapperStyle={{ zIndex: 100 }}
            />
            <ReferenceLine
              yAxisId="percent"
              y={80}
              stroke="#ef4444"
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{
                value: '80% Line',
                position: 'right',
                fontSize: 11,
                fill: '#ef4444',
                fontWeight: 600,
              }}
            />
            <Bar
              yAxisId="value"
              dataKey="value"
              name="value"
              radius={[4, 4, 0, 0]}
              barSize={32}
            >
              {paretoData.map((entry, index) => {
                const colors = PARETO_BAR_COLORS[metric];
                return (
                  <Cell
                    key={`pareto-bar-${index}`}
                    fill={entry.isVitalFew
                      ? colors[Math.min(index, colors.length - 1)]
                      : '#e5e7eb'
                    }
                    opacity={entry.isVitalFew ? 1 : 0.6}
                  />
                );
              })}
            </Bar>
            <Line
              yAxisId="percent"
              type="monotone"
              dataKey="cumulativePercent"
              name="cumulativePercent"
              stroke={config.color}
              strokeWidth={2.5}
              dot={{ fill: config.color, r: 4, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7, fill: config.color }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Data matrix table with month columns + smart tooltips */}
      {(() => {
        // Build month keys for the range (oldest-first for table left-to-right)
        const now = new Date();
        const monthKeys: { key: string; label: string; longLabel: string }[] = [];
        for (let i = rangeMonths - 1; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
          const longLabel = d.toLocaleString('default', { month: 'long', year: 'numeric' });
          monthKeys.push({ key, label, longLabel });
        }

        // Build TWO lookups per project:
        //   1. monthlyLookup   → projectId → monthKey → metric value
        //   2. monthlyIncomeLk → projectId → monthKey → constructionIncome (for % of income calc)
        const monthlyLookup = new Map<string, Map<string, number>>();
        const monthlyIncomeLk = new Map<string, Map<string, number>>();
        if (projectMonthlyData) {
          for (const pm of projectMonthlyData) {
            const metricMap = new Map<string, number>();
            const incomeMap = new Map<string, number>();
            for (const mr of pm.monthlyResults) {
              let val = 0;
              switch (metric) {
                case 'cost': val = mr.result.totalDeductions; break;
                case 'income': val = mr.result.constructionIncome; break;
                case 'profit': val = mr.result.netProfit; break;
                case 'patching': val = mr.result.expensesByCategory['Patching'] || 0; break;
              }
              metricMap.set(mr.key, val);
              incomeMap.set(mr.key, mr.result.constructionIncome);
            }
            monthlyLookup.set(pm.projectId, metricMap);
            monthlyIncomeLk.set(pm.projectId, incomeMap);
          }
        }

        // Find project IDs from filteredData to match paretoData order
        const projectIdMap = new Map<string, string>();
        for (const p of filteredData) {
          projectIdMap.set(p.projectName, p.projectId);
        }

        // Helper: full dollar format for tooltips
        const fmtFull = (v: number) => fmt.currency(v, true);
        const fmtPct = (v: number) => `${v.toFixed(2)}%`;

        // Smart tooltip builders
        const buildMonthTooltip = (projectName: string, monthLabel: string, val: number, monthIncome: number): string => {
          const lines = [
            `📍 ${projectName} · ${monthLabel}`,
            ``,
            `${config.label}: ${fmtFull(val)}`,
          ];
          if (metric !== 'income' && monthIncome > 0) {
            const pct = (val / monthIncome) * 100;
            lines.push(`Income that month: ${fmtFull(monthIncome)}`);
            lines.push(`% of Income: ${fmtPct(pct)}`);
            lines.push(`  → ${fmtFull(val)} ÷ ${fmtFull(monthIncome)} = ${fmtPct(pct)}`);
          }
          if (metric === 'income') {
            const totalPortfolioIncome = filteredData.reduce((s, p) => s + p.constructionIncome, 0);
            if (totalPortfolioIncome > 0) {
              lines.push(`% of Portfolio Income: ${fmtPct((val / totalPortfolioIncome) * 100)}`);
            }
          }
          return lines.join('\n');
        };

        const buildTotalTooltip = (d: typeof paretoData[number]): string => {
          const lines = [
            `📊 ${d.fullName} · Total (${rangeMonths} months)`,
            ``,
            `${config.label}: ${fmtFull(d.value)}`,
          ];
          if (metric !== 'income' && d.income > 0) {
            const pct = (d.value / d.income) * 100;
            lines.push(`Total Income: ${fmtFull(d.income)}`);
            lines.push(`% of Income: ${fmtPct(pct)}`);
            lines.push(`  → ${fmtFull(d.value)} ÷ ${fmtFull(d.income)} = ${fmtPct(pct)}`);
          }
          if (metric === 'income') {
            const totalPortfolioIncome = filteredData.reduce((s, p) => s + p.constructionIncome, 0);
            if (totalPortfolioIncome > 0) {
              lines.push(`% of All Projects: ${fmtPct((d.value / totalPortfolioIncome) * 100)}`);
            }
          }
          lines.push(`% of Total ${config.label}: ${fmtPct(d.individualPercent)}`);
          lines.push(`  → ${fmtFull(d.value)} ÷ ${fmtFull(totalValue)} = ${fmtPct(d.individualPercent)}`);
          return lines.join('\n');
        };

        const buildPatchingPctTooltip = (d: typeof paretoData[number]): string => {
          if (d.income <= 0) return `No income recorded, defect ratio unavailable`;
          const patchVal = d.value; // in patching mode, d.value IS the patching cost
          return [
            `🔍 Defect Ratio Calculation`,
            ``,
            `Project: ${d.fullName}`,
            `Patching Cost: ${fmtFull(patchVal)}`,
            `Construction Income: ${fmtFull(d.income)}`,
            ``,
            `Formula: (Patching ÷ Income) × 100`,
            `  → (${fmtFull(patchVal)} ÷ ${fmtFull(d.income)}) × 100`,
            `  = ${fmtPct(d.patchingPercent)}`,
            ``,
            d.patchingPercent > 3 ? `🔴 Above 3%, critical quality concern`
              : d.patchingPercent > 1.5 ? `🟠 Between 1.5-3%, needs attention`
              : `🟢 Below 1.5%, healthy defect ratio`,
          ].join('\n');
        };

        const buildPctOfTotalTooltip = (d: typeof paretoData[number]): string => {
          return [
            `Share of Total ${config.label}`,
            ``,
            `${d.fullName}: ${fmtFull(d.value)}`,
            `All projects total: ${fmtFull(totalValue)}`,
            ``,
            `${fmtFull(d.value)} ÷ ${fmtFull(totalValue)} = ${fmtPct(d.individualPercent)}`,
            ``,
            `This project contributes ${d.individualPercent.toFixed(1)}% of the total ${config.label.toLowerCase()} across all projects.`,
          ].join('\n');
        };

        const buildCumPctTooltip = (d: typeof paretoData[number], idx: number): string => {
          const projectsSoFar = paretoData.slice(0, idx + 1);
          const runningSum = projectsSoFar.reduce((s, p) => s + p.value, 0);
          return [
            `Cumulative ${config.label} · Top ${idx + 1} Project${idx > 0 ? 's' : ''}`,
            ``,
            `Running total: ${fmtFull(runningSum)}`,
            `All projects total: ${fmtFull(totalValue)}`,
            ``,
            `${fmtFull(runningSum)} ÷ ${fmtFull(totalValue)} = ${fmtPct(d.cumulativePercent)}`,
            ``,
            d.cumulativePercent <= 80
              ? `⚡ Within the "Vital Few": the top projects driving 80% of ${config.label.toLowerCase()}.`
              : `This project falls in the "Trivial Many": individually small contribution.`,
          ].join('\n');
        };

        const thStyle: React.CSSProperties = {
          textAlign: 'right', padding: '5px 6px', fontSize: '0.7rem',
          color: 'var(--text-secondary, #6b7280)', fontWeight: 600, whiteSpace: 'nowrap',
        };
        const tdStyle: React.CSSProperties = {
          textAlign: 'right', padding: '5px 6px', fontSize: '0.72rem',
          fontFamily: 'monospace', whiteSpace: 'nowrap', cursor: 'help',
        };

        // ── XLSX Export ────────────────────────────────────────────────────
        const isPatching = metric === 'patching';

        const exportParetoToXlsx = () => {
          const wb = XLSX.utils.book_new();

          // Build header row(s)
          const headerRow1: string[] = ['Project'];
          if (isPatching) {
            for (const mk of monthKeys) {
              headerRow1.push(mk.longLabel, '% Inc');
            }
          } else {
            for (const mk of monthKeys) {
              headerRow1.push(mk.longLabel);
            }
          }
          headerRow1.push(`Total (${rangeMonths}mo)`);
          if (isPatching) headerRow1.push('Defect Ratio %');
          if (!isPatching) {
            headerRow1.push('% of Total', 'Cumulative %');
          }
          headerRow1.push('Category');

          // Build data rows
          const rows: (string | number)[][] = [headerRow1];
          for (const d of paretoData) {
            const pid = projectIdMap.get(d.fullName) || '';
            const mMap = monthlyLookup.get(pid);
            const incMap = monthlyIncomeLk.get(pid);
            const row: (string | number)[] = [d.fullName];
            for (const mk of monthKeys) {
              const val = mMap?.get(mk.key) || 0;
              row.push(val);
              if (isPatching) {
                const inc = incMap?.get(mk.key) || 0;
                const pct = inc > 0 ? Math.round(((val / inc) * 100) * 10) / 10 : 0;
                row.push(pct);
              }
            }
            row.push(d.value);
            if (isPatching) row.push(Math.round(d.patchingPercent * 10) / 10);
            if (!isPatching) {
              row.push(d.individualPercent);
              row.push(d.cumulativePercent);
            }
            row.push(d.isVitalFew ? 'Vital' : 'Trivial');
            rows.push(row);
          }

          // SEQ-180 phase 2 (security audit): a project name is free text, and
          // every spreadsheet treats a cell starting with '=', '+', '-' or '@'
          // as a FORMULA. Column one of every data row below is d.fullName, so
          // this export had the same exposure the P&L analytics table did.
          // aoa_to_sheet takes the rows exactly as given, so they are made
          // safe before the sheet is built, using the same helper both export
          // paths share. Numbers are untouched and still sum in the sheet.
          const safeRows = xlsxAoaRows(rows);
          const ws = XLSX.utils.aoa_to_sheet(safeRows);

          // Auto-fit column widths, measured on the SAFE rows so a
          // neutralised cell's extra character is accounted for.
          const colWidths = headerRow1.map((h, ci) => {
            let maxLen = String(h).length;
            for (const r of safeRows.slice(1)) {
              const cellLen = String(r[ci] ?? '').length;
              if (cellLen > maxLen) maxLen = cellLen;
            }
            return { wch: Math.min(maxLen + 3, 28) };
          });
          ws['!cols'] = colWidths;

          const sheetName = `${config.label} Pareto`;
          XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));

          const today = new Date().toISOString().split('T')[0];
          XLSX.writeFile(wb, `Pareto_${config.label}_${rangeMonths}mo_${today}.xlsx`);
        };

        // Defect-ratio color helper
        const defectColor = (pct: number) =>
          pct > 3 ? '#ef4444' : pct > 1.5 ? '#f97316' : '#10b981';

        return (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <div style={{
              fontSize: '0.72rem', color: 'var(--text-muted, #9ca3af)',
              padding: '0 6px 6px', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                background: config.color, opacity: 0.6,
              }} />
              {config.label} breakdown
              <span style={{ opacity: 0.5 }}>
                · {monthKeys[0]?.label} to {monthKeys[monthKeys.length - 1]?.label}
              </span>
              {/* SEQ-180 phase 2 (React review, Safe Mode): an export is not a
                  screen share. The file keeps its dollar figures long after
                  Safe Mode is switched back off, so the button is disabled
                  outright rather than writing masked strings into a
                  spreadsheet, matching what the P&L analytics table already
                  does with its own two export buttons. */}
              <button
                onClick={exportParetoToXlsx}
                disabled={isSafeModeActive()}
                style={{
                  marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.65rem', fontWeight: 600, padding: '3px 10px', borderRadius: 8,
                  border: `1px solid ${config.borderColor}`, background: config.lightBg,
                  color: config.color, transition: 'all 0.15s',
                  cursor: isSafeModeActive() ? 'not-allowed' : 'pointer',
                  opacity: isSafeModeActive() ? 0.5 : 1,
                }}
                title={isSafeModeActive()
                  ? 'Exports are off while Safe Mode is on'
                  : `Export this ${config.label} Pareto table to Excel (.xlsx)\nIncludes all ${paretoData.length} projects × ${rangeMonths} months`}
              >
                <Download size={12} />
                Export XLSX
              </button>
              <span style={{ opacity: 0.35, fontSize: '0.65rem' }}>
                💡 Hover any value for details
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <thead>
                {/* Row 1: main headers (month headers get colspan 2 for patching) */}
                <tr style={{ borderBottom: isPatching ? 'none' : '2px solid var(--border-color, #e5e7eb)' }}>
                  <th style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--bg-secondary, #fff)', zIndex: 2 }}
                    rowSpan={isPatching ? 2 : 1}>Project</th>
                  {monthKeys.map(mk => (
                    <th key={mk.key} style={{ ...thStyle, textAlign: 'center' }} title={mk.longLabel}
                      colSpan={isPatching ? 2 : 1}>{mk.label}</th>
                  ))}
                  <th style={{ ...thStyle, borderLeft: '2px solid var(--border-color, #e5e7eb)', color: config.color }}
                    rowSpan={isPatching ? 2 : 1}>
                    <span className="hwo-heading">
                      Total
                      <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.col.total"]} />
                    </span>
                  </th>
                  {isPatching && (
                    <th style={{ ...thStyle, color: '#f97316' }}
                      rowSpan={2}>
                      <span className="hwo-heading">
                        % Inc
                        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.col.defect-ratio"]} />
                      </span>
                    </th>
                  )}
                  {!isPatching && (
                    <th style={thStyle}>
                      <span className="hwo-heading">
                        % Tot
                        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.col.share"]} />
                      </span>
                    </th>
                  )}
                  {!isPatching && (
                    <th style={thStyle}>
                      <span className="hwo-heading">
                        Cum %
                        <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.col.cumulative"]} />
                      </span>
                    </th>
                  )}
                  <th style={{ ...thStyle, textAlign: 'center' }} rowSpan={isPatching ? 2 : 1}>
                    <span className="hwo-heading">
                      Cat
                      <HowWorkedOut definition={PNL_DASHBOARD_FIGURES["pnl.pareto.col.category"]} />
                    </span>
                  </th>
                </tr>
                {/* Row 2: sub-headers for patching dual-columns ($ | %) */}
                {isPatching && (
                  <tr style={{ borderBottom: '2px solid var(--border-color, #e5e7eb)' }}>
                    {monthKeys.map(mk => (
                      <React.Fragment key={`sub-${mk.key}`}>
                        <th style={{ ...thStyle, fontSize: '0.6rem', opacity: 0.6, padding: '2px 4px' }}>$</th>
                        <th style={{ ...thStyle, fontSize: '0.6rem', opacity: 0.6, padding: '2px 4px', color: '#f97316' }}>%</th>
                      </React.Fragment>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {paretoData.map((d, i) => {
                  const pid = projectIdMap.get(d.fullName) || '';
                  const mMap = monthlyLookup.get(pid);
                  const incMap = monthlyIncomeLk.get(pid);
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid var(--border-color, #f3f4f6)',
                        background: d.isVitalFew ? config.lightBg : 'transparent',
                      }}
                    >
                      <td style={{
                        padding: '5px 6px', fontWeight: d.isVitalFew ? 500 : 400,
                        fontSize: '0.72rem', position: 'sticky', left: 0,
                        background: d.isVitalFew ? config.lightBg : 'var(--bg-secondary, #fff)',
                        zIndex: 1, whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                        title={d.fullName}
                      >
                        {d.fullName}
                      </td>
                      {monthKeys.map(mk => {
                        const val = mMap?.get(mk.key) || 0;
                        const monthIncome = incMap?.get(mk.key) || 0;
                        const monthDefect = isPatching && monthIncome > 0 ? (val / monthIncome) * 100 : 0;
                        return (
                          <React.Fragment key={mk.key}>
                            <td style={{
                              ...tdStyle,
                              color: val === 0 ? 'var(--text-muted, #d1d5db)' : 'var(--text-primary, #111827)',
                            }}
                              title={val === 0
                                ? `${d.fullName} · ${mk.longLabel}\nNo ${config.label.toLowerCase()} recorded this month.`
                                : buildMonthTooltip(d.fullName, mk.longLabel, val, monthIncome)
                              }
                            >
                              {val === 0 ? '—' : fmt.compact(val)}
                            </td>
                            {isPatching && (
                              <td style={{
                                ...tdStyle, fontSize: '0.65rem', fontWeight: 600,
                                color: val === 0 ? 'var(--text-muted, #d1d5db)' : defectColor(monthDefect),
                              }}
                                title={val === 0
                                  ? `No patching this month`
                                  : `🔍 ${d.fullName} · ${mk.longLabel}\n\nPatching: ${fmtFull(val)}\nIncome: ${fmtFull(monthIncome)}\nDefect Ratio: ${fmtPct(monthDefect)}\n\n${monthDefect > 3 ? '🔴 Critical' : monthDefect > 1.5 ? '🟠 Needs attention' : '🟢 Healthy'}`
                                }
                              >
                                {val === 0 ? '—' : `${monthDefect.toFixed(1)}%`}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <td style={{
                        ...tdStyle, fontWeight: 600,
                        borderLeft: '2px solid var(--border-color, #e5e7eb)',
                        color: config.color,
                      }}
                        title={buildTotalTooltip(d)}
                      >
                        {fmt.compact(d.value)}
                      </td>
                      {isPatching && (
                        <td style={{
                          ...tdStyle, fontWeight: 600,
                          color: d.patchingPercent > 3 ? '#ef4444' : d.patchingPercent > 1.5 ? '#f97316' : '#10b981',
                        }}
                          title={buildPatchingPctTooltip(d)}
                        >
                          {d.patchingPercent.toFixed(1)}%
                        </td>
                      )}
                      {!isPatching && (
                        <td style={tdStyle} title={buildPctOfTotalTooltip(d)}>
                          {d.individualPercent.toFixed(1)}%
                        </td>
                      )}
                      {!isPatching && (
                        <td style={{ ...tdStyle, fontWeight: 600, color: d.cumulativePercent <= 80 ? config.color : '#6b7280' }}
                          title={buildCumPctTooltip(d, i)}
                        >
                          {d.cumulativePercent.toFixed(1)}%
                        </td>
                      )}
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          fontSize: '0.65rem', padding: '1px 6px', borderRadius: 12, fontWeight: 600,
                          background: d.isVitalFew ? config.lightBg : '#f3f4f6',
                          color: d.isVitalFew ? config.color : '#9ca3af',
                          border: `1px solid ${d.isVitalFew ? config.borderColor : '#e5e7eb'}`,
                          cursor: 'help',
                        }}
                          title={d.isVitalFew
                            ? `⚡ Vital Few. This project is among the top ${vitalFewCount} that together account for ~80% of total ${config.label.toLowerCase()}. These deserve the most management attention.`
                            : `Trivial Many. This project contributes only ${d.individualPercent.toFixed(1)}% of total ${config.label.toLowerCase()}. Important, but individual impact is limited.`
                          }
                        >
                          {d.isVitalFew ? 'Vital' : 'Trivial'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      <ChartInfoPanel
        title={`Pareto Analysis: ${config.label} View (80/20 Rule)`}
        {...infoContent}
      />
    </div>
  );
}
